import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { SEO_CONFIG, type PageSEO } from '@/lib/seo';
import type { Job } from '@/types/job';

const BASE_URL = 'https://www.xpendmarketing.com';

function useJobSEO(pathname: string): PageSEO | null {
  const [seo, setSeo] = useState<PageSEO | null>(null);
  const jobMatch = pathname.match(/^\/careers\/(\d+)$/);

  useEffect(() => {
    if (!jobMatch) {
      setSeo(null);
      return;
    }

    let cancelled = false;
    const jobId = jobMatch[1];

    fetch(`/api/jobs/${jobId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((job: Job | null) => {
        if (cancelled || !job) return;
        setSeo({
          title: `${job.title} - Careers - Xpend Marketing`,
          description: `Apply for ${job.title} at Xpend Marketing. ${job.department} · ${job.location} · ${job.type}.`,
          canonical: `${BASE_URL}/careers/${job.id}`,
          ogImage: `${BASE_URL}/logo-dark.png`,
        });
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [pathname, jobMatch?.[1]]);

  return seo;
}

export default function SEOHead() {
  const { pathname } = useLocation();
  const jobSeo = useJobSEO(pathname);
  const seo = jobSeo || SEO_CONFIG[pathname] || SEO_CONFIG['/'];

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <link rel="canonical" href={seo.canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonical} />
      {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
      <meta property="og:site_name" content="Xpend Marketing" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}
    </Helmet>
  );
}
